import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  blobErrorText,
  useLazyExportPtoVolumeEditLogXlsxQuery,
  useListPtoVolumeEditLogInfiniteQuery,
  type PtoRdDocument,
  type PtoVolumeEditLogFilter,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import { LogEntry } from './LogEntry';
import { journalActions, journalActors, logEntryView, mergeLogPages, todayIso } from './logic';
import { useRdVolumeEditAccess } from './useRdVolumeEditAccess';
import { ErrorText, FieldLabel, Muted, saveBlob } from './ui';

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Filters = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr)) auto;
  gap: ${({ theme }) => theme.spacing(2.5)};
  align-items: end;
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const More = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(3)} 0;
`;

/**
 * Журнал правок РД проекта (№8): неудаляемый — только добавление. Фильтры
 * (комплект, действие, кто, период), подгрузка при прокрутке (курсор
 * `before_id`), «Выгрузить в Excel» с тем же отбором. У записи — кто внёс (ФИО,
 * роль), когда, «было → стало», лист, причина. Виден всем, кто читает проект.
 */
export function EditJournalPanel({
  projectId,
  documents,
}: {
  projectId: string;
  documents: readonly PtoRdDocument[];
}) {
  const rights = useRdVolumeEditAccess(projectId);
  const [documentId, setDocumentId] = useState('');
  const [action, setAction] = useState('');
  const [actorId, setActorId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const filter: PtoVolumeEditLogFilter = useMemo(
    () => ({
      document_id: documentId || undefined,
      action: action || undefined,
      actor_id: actorId || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [documentId, action, actorId, from, to],
  );
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useListPtoVolumeEditLogInfiniteQuery({ projectId, ...filter }, { skip: !projectId });
  const entries = useMemo(() => mergeLogPages(data?.pages), [data]);
  // Авторы для фильтра «Кто» — все, кто встречался в журнале за эту сессию:
  // выбор автора не сужает список.
  const [actors, setActors] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    const found = journalActors(entries);
    setActors((prev) => {
      const by = new Map(prev.map((a) => [a.id, a]));
      let changed = false;
      for (const a of found) {
        if (!by.has(a.id)) {
          by.set(a.id, a);
          changed = true;
        }
      }
      return changed ? [...by.values()].sort((a, b) => a.name.localeCompare(b.name, 'ru')) : prev;
    });
  }, [entries]);

  // Подгрузка при прокрутке: метка в конце списка видна — следующая страница.
  const sentinel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasNextPage || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((list) => {
      if (list.some((e) => e.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const [exportXlsx, { isFetching: exporting }] = useLazyExportPtoVolumeEditLogXlsxQuery();
  const [exportError, setExportError] = useState<string | null>(null);
  async function onExport() {
    setExportError(null);
    try {
      const blob = await exportXlsx({ projectId, ...filter }).unwrap();
      saveBlob(blob, `Журнал правок РД ${todayIso()}.xlsx`);
    } catch (e) {
      setExportError(await blobErrorText(e, 'Журнал не выгружен.'));
    }
  }

  const sortedDocs = useMemo(
    () => [...documents].sort((a, b) => a.code.localeCompare(b.code, 'ru')),
    [documents],
  );
  const filtered = Boolean(documentId || action || actorId || from || to);

  return (
    <div>
      <Head>
        <div>
          <h3 style={{ margin: 0 }}>Журнал правок РД</h3>
          <Muted>
            Кто внёс, когда, «было → стало», лист и причина. Журнал только дополняется: записи не
            изменяются и не удаляются.
          </Muted>
        </div>
        <Button
          $variant="secondary"
          $size="sm"
          disabled={exporting || entries.length === 0}
          onClick={() => void onExport()}
        >
          {exporting ? 'Формирование…' : 'Выгрузить в Excel'}
        </Button>
      </Head>
      {exportError && <ErrorText style={{ marginBottom: 8 }}>{exportError}</ErrorText>}
      <Filters>
        <FieldLabel htmlFor="jr-doc">
          Комплект
          <Select id="jr-doc" value={documentId} onChange={(e) => setDocumentId(e.target.value)}>
            <option value="">Все</option>
            {sortedDocs.map((d) => (
              <option key={d.id} value={d.id}>
                {d.code}
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel htmlFor="jr-action">
          Действие
          <Select id="jr-action" value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">Все</option>
            {journalActions().map((a) => (
              <option key={a.code} value={a.code}>
                {a.label}
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel htmlFor="jr-actor">
          Кто
          <Select id="jr-actor" value={actorId} onChange={(e) => setActorId(e.target.value)}>
            <option value="">Все</option>
            {actors.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel htmlFor="jr-from">
          С даты
          <Input id="jr-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </FieldLabel>
        <FieldLabel htmlFor="jr-to">
          По дату
          <Input id="jr-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </FieldLabel>
        {filtered ? (
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            onClick={() => {
              setDocumentId('');
              setAction('');
              setActorId('');
              setFrom('');
              setTo('');
            }}
          >
            Сбросить
          </Button>
        ) : (
          <span />
        )}
      </Filters>

      {isLoading ? (
        <Muted>Загрузка журнала…</Muted>
      ) : error ? (
        <ErrorText>{apiErrorText(error, 'Не удалось загрузить журнал правок.')}</ErrorText>
      ) : entries.length === 0 ? (
        <Muted>{filtered ? 'По отбору записей нет.' : 'Записей в журнале пока нет.'}</Muted>
      ) : (
        <List>
          {entries.map((e) => (
            <LogEntry key={e.id} view={logEntryView(e, rights.access)} />
          ))}
        </List>
      )}
      <div ref={sentinel} />
      {hasNextPage && (
        <More>
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
          </Button>
        </More>
      )}
    </div>
  );
}
